import { useState } from 'react';
import {
  Star,
  Check,
  ArrowRight,
  Building2,
  Heart,
  Sparkles,
  Wrench,
  MessageCircle,
  ShieldCheck,
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Toaster, toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StarRating = ({ value, onChange, testId }) => (
  <div className="flex items-center gap-2" data-testid={testId}>
    {[1, 2, 3, 4, 5].map((n) => {
      const active = value >= n;
      return (
        <button
          type="button"
          key={n}
          onClick={() => onChange(n)}
          onMouseEnter={() => {}}
          className="p-1 group"
          data-testid={`${testId}-star-${n}`}
          aria-label={`${n} star`}
        >
          <Star
            className={`w-8 h-8 transition-all ${
              active
                ? 'fill-[#D4AF37] text-[#D4AF37]'
                : 'text-zinc-700 group-hover:text-[#D4AF37]/60'
            }`}
          />
        </button>
      );
    })}
    <span className="ml-2 text-zinc-500 font-manrope text-sm">
      {value ? `${value} / 5` : 'Tap to rate'}
    </span>
  </div>
);

const YesNo = ({ value, onChange, name }) => (
  <div className="flex gap-2" data-testid={`yesno-${name}`}>
    {['Yes', 'No'].map((label) => {
      const selected = value === (label === 'Yes');
      return (
        <button
          type="button"
          key={label}
          onClick={() => onChange(label === 'Yes')}
          className={`min-w-[92px] px-6 py-2.5 rounded-full font-manrope text-sm font-medium border transition-all ${
            selected
              ? 'bg-[#D4AF37] text-[#0A0A0A] border-[#D4AF37]'
              : 'bg-white/5 text-zinc-300 border-white/10 hover:border-[#D4AF37]/40 hover:text-white'
          }`}
          data-testid={`${name}-${label.toLowerCase()}`}
        >
          {label}
        </button>
      );
    })}
  </div>
);

const SectionHeader = ({ icon: Icon, eyebrow, title }) => (
  <div className="flex items-start gap-4 mb-5">
    <div className="w-11 h-11 rounded-xl bg-[#D4AF37]/15 border border-[#D4AF37]/25 flex items-center justify-center flex-shrink-0">
      <Icon className="w-5 h-5 text-[#D4AF37]" />
    </div>
    <div>
      <p className="text-[#D4AF37] font-manrope uppercase tracking-[0.2em] text-[10px] font-bold mb-1">
        {eyebrow}
      </p>
      <h3 className="font-cormorant text-2xl md:text-3xl font-medium text-white leading-tight">
        {title}
      </h3>
    </div>
  </div>
);

export default function FeedbackPage() {
  const [overall, setOverall] = useState(0);
  const [recommend, setRecommend] = useState(null);
  const [venueRating, setVenueRating] = useState(0);
  const [venueComments, setVenueComments] = useState('');
  const [favorite, setFavorite] = useState('');
  const [nextYear, setNextYear] = useState('');
  const [improvements, setImprovements] = useState('');
  const [additional, setAdditional] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!overall || !venueRating) {
      toast.error('Please rate the retreat and the venue');
      return;
    }
    if (recommend === null) {
      toast.error("Please tell us if you'd recommend the retreat");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/feedback/retreat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          overall_rating: overall,
          would_recommend: recommend,
          venue_rating: venueRating,
          venue_comments: venueComments || null,
          favorite_session: favorite || null,
          next_year_ideas: nextYear || null,
          improvements: improvements || null,
          additional_comments: additional || null,
          name: name || null,
          email: email || null,
        }),
      });
      if (res.ok) {
        setSubmitted(true);
        toast.success('Thank you — your feedback shapes what comes next.');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not submit. Please try again.');
      }
    } catch (err) {
      toast.error('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#0A0A0A] text-white flex flex-col"
      data-testid="feedback-page"
    >
      <Toaster position="top-center" richColors />

      {/* Header */}
      <header className="border-b border-white/5 bg-[#0A0A0A]/80 backdrop-blur-md">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3" data-testid="header-home-link">
            <div className="w-9 h-9 rounded-lg bg-[#D4AF37] text-[#0A0A0A] flex items-center justify-center font-cormorant font-bold text-lg">
              R
            </div>
            <div className="hidden sm:block">
              <p className="text-white font-cormorant text-lg leading-tight">The Reset</p>
              <p className="text-zinc-500 font-manrope text-[10px] uppercase tracking-widest">
                July 23–26, 2026
              </p>
            </div>
          </a>
          <a
            href="/"
            className="text-zinc-400 hover:text-[#D4AF37] font-manrope text-sm transition-colors"
          >
            ← Back to home
          </a>
        </div>
      </header>

      <main className="flex-1 py-16 px-6">
        <div className="max-w-3xl mx-auto">
          {/* Hero */}
          <div className="text-center mb-12">
            <p className="text-[#D4AF37] font-manrope uppercase tracking-[0.3em] text-xs font-bold mb-4">
              The Reset · Post-Retreat Feedback
            </p>
            <h1
              className="font-cormorant text-4xl md:text-6xl font-medium text-white leading-[1.1] mb-4"
              data-testid="feedback-hero-title"
            >
              You showed up. <br />
              <span className="italic text-[#D4AF37]">Now shape what&apos;s next.</span>
            </h1>
            <p className="text-zinc-400 font-manrope text-lg max-w-xl mx-auto leading-relaxed">
              Your honest reflection helps us build a better experience for the next brotherhood.
              Five minutes. All questions optional except the ratings.
            </p>
          </div>

          {submitted ? (
            <div
              className="bg-[#111111] border border-[#D4AF37]/30 rounded-2xl p-12 text-center"
              data-testid="feedback-success"
            >
              <div className="w-16 h-16 rounded-full bg-[#D4AF37]/15 flex items-center justify-center mx-auto mb-6">
                <Check className="w-8 h-8 text-[#D4AF37]" />
              </div>
              <h2 className="font-cormorant text-3xl font-medium text-white mb-3">
                Thank you, brother.
              </h2>
              <p className="text-zinc-400 font-manrope max-w-md mx-auto">
                Every word you shared shapes what The Reset becomes next year. We&apos;ll be in
                touch — keep rising.
              </p>
              <a
                href="/"
                className="inline-block mt-8 bg-[#D4AF37] text-[#0A0A0A] px-8 py-3 rounded-full font-manrope font-semibold hover:bg-amber-200 transition-colors"
                data-testid="feedback-home-btn"
              >
                Back to The Reset
              </a>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="space-y-6"
              data-testid="feedback-form"
            >
              {/* 1. Overall */}
              <div className="bg-[#111111] border border-white/10 rounded-2xl p-6 md:p-8">
                <SectionHeader
                  icon={Sparkles}
                  eyebrow="Section 1"
                  title="Your overall experience"
                />
                <div className="space-y-6">
                  <div>
                    <p className="text-zinc-300 font-manrope text-sm mb-3">
                      How would you rate The Reset overall?
                    </p>
                    <StarRating
                      value={overall}
                      onChange={setOverall}
                      testId="overall-rating"
                    />
                  </div>
                  <div>
                    <p className="text-zinc-300 font-manrope text-sm mb-3">
                      Would you recommend The Reset to another brother?
                    </p>
                    <YesNo value={recommend} onChange={setRecommend} name="recommend" />
                  </div>
                </div>
              </div>

              {/* 2. Venue */}
              <div className="bg-[#111111] border border-white/10 rounded-2xl p-6 md:p-8">
                <SectionHeader
                  icon={Building2}
                  eyebrow="Section 2"
                  title="The Westin Playa Bonita"
                />
                <p className="text-zinc-400 font-manrope text-sm mb-5 -mt-2">
                  Tell us how the venue held up — rooms, service, food, beach access, the whole feel.
                </p>
                <div className="space-y-5">
                  <div>
                    <p className="text-zinc-300 font-manrope text-sm mb-3">
                      Rate the venue
                    </p>
                    <StarRating
                      value={venueRating}
                      onChange={setVenueRating}
                      testId="venue-rating"
                    />
                  </div>
                  <textarea
                    value={venueComments}
                    onChange={(e) => setVenueComments(e.target.value)}
                    rows={4}
                    placeholder="What worked? What didn't? Any specifics we should know for next year…"
                    className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg text-white placeholder:text-zinc-600 p-4 font-manrope text-sm resize-y focus:outline-none focus:border-[#D4AF37]/40"
                    data-testid="venue-comments-input"
                  />
                </div>
              </div>

              {/* 3. Favorite session */}
              <div className="bg-[#111111] border border-white/10 rounded-2xl p-6 md:p-8">
                <SectionHeader
                  icon={Heart}
                  eyebrow="Section 3"
                  title="What moved you most?"
                />
                <textarea
                  value={favorite}
                  onChange={(e) => setFavorite(e.target.value)}
                  rows={4}
                  placeholder="Which session, moment, or conversation stuck with you? What was the takeaway you're carrying home?"
                  className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg text-white placeholder:text-zinc-600 p-4 font-manrope text-sm resize-y focus:outline-none focus:border-[#D4AF37]/40"
                  data-testid="favorite-session-input"
                />
              </div>

              {/* 4. Next year ideas */}
              <div className="bg-[#111111] border border-white/10 rounded-2xl p-6 md:p-8">
                <SectionHeader
                  icon={Sparkles}
                  eyebrow="Section 4"
                  title="Feedback for next year"
                />
                <p className="text-zinc-400 font-manrope text-sm mb-5 -mt-2">
                  Topics you&apos;d love to see, facilitators to bring in, format ideas, destinations…
                </p>
                <textarea
                  value={nextYear}
                  onChange={(e) => setNextYear(e.target.value)}
                  rows={4}
                  placeholder="What should The Reset 2027 include? Dream big, be specific."
                  className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg text-white placeholder:text-zinc-600 p-4 font-manrope text-sm resize-y focus:outline-none focus:border-[#D4AF37]/40"
                  data-testid="next-year-input"
                />
              </div>

              {/* 5. Improvements */}
              <div className="bg-[#111111] border border-white/10 rounded-2xl p-6 md:p-8">
                <SectionHeader
                  icon={Wrench}
                  eyebrow="Section 5"
                  title="Opportunities for improvement"
                />
                <p className="text-zinc-400 font-manrope text-sm mb-5 -mt-2">
                  Be honest. What fell short? What could we do better?
                </p>
                <textarea
                  value={improvements}
                  onChange={(e) => setImprovements(e.target.value)}
                  rows={4}
                  placeholder="Scheduling, pacing, sessions, meals, travel logistics — no filter needed."
                  className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg text-white placeholder:text-zinc-600 p-4 font-manrope text-sm resize-y focus:outline-none focus:border-[#D4AF37]/40"
                  data-testid="improvements-input"
                />
              </div>

              {/* 6. Additional comments */}
              <div className="bg-[#111111] border border-white/10 rounded-2xl p-6 md:p-8">
                <SectionHeader
                  icon={MessageCircle}
                  eyebrow="Section 6"
                  title="Anything else on your mind?"
                />
                <textarea
                  value={additional}
                  onChange={(e) => setAdditional(e.target.value)}
                  rows={4}
                  placeholder="Comments, gratitude, hard truths, ideas we didn't ask about — the floor is yours."
                  className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg text-white placeholder:text-zinc-600 p-4 font-manrope text-sm resize-y focus:outline-none focus:border-[#D4AF37]/40"
                  data-testid="additional-input"
                />
              </div>

              {/* Optional identity */}
              <div className="bg-[#111111] border border-white/10 rounded-2xl p-6 md:p-8">
                <SectionHeader
                  icon={ShieldCheck}
                  eyebrow="Optional"
                  title="Want us to follow up?"
                />
                <p className="text-zinc-400 font-manrope text-sm mb-5 -mt-2">
                  Leave these blank to stay anonymous. Add them if you&apos;d like a response from the
                  Reset team.
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <Input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name (optional)"
                    className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-zinc-600 h-12"
                    data-testid="feedback-name-input"
                  />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email (optional)"
                    className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-zinc-600 h-12"
                    data-testid="feedback-email-input"
                  />
                </div>
              </div>

              {/* Submit */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2">
                <p className="text-zinc-500 font-manrope text-xs flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-[#D4AF37]" />
                  Confidential · we never share your responses.
                </p>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-[#D4AF37] text-[#0A0A0A] px-10 h-12 rounded-full font-manrope font-semibold hover:bg-amber-200 disabled:opacity-50 group"
                  data-testid="feedback-submit-btn"
                >
                  {submitting ? 'Submitting…' : (
                    <>
                      Submit feedback
                      <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform" />
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
        </div>
      </main>

      <footer className="border-t border-white/5 py-8 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-zinc-600 font-manrope text-xs">
            The Reset 2026 · July 23–26 · The Westin Playa Bonita · Panama City, Panama
          </p>
        </div>
      </footer>
    </div>
  );
}
